import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import {
  Calendar,
  ChevronDown,
  CurrencyDollar,
  Datepiker,
  Magic,
  Picture,
  Target,
  UserCircle,
  VisualIdentity,
} from '../../assets/svgs/icons'
import { useAuth } from '../../hooks/useAuth'
import { useNavigateWithValidation } from '../../hooks/useNavigateWithValidation'
import { PublicationService } from '../../services'
import ModalValidDowngrade from '../ModalValidDowngrade'

const NAV_ICON_COLOR = 'rgba(65, 65, 65, 0.73)'
const NAV_ICON_ACTIVE = 'rgba(188, 0, 238, 1)'
const NAV_ICON_SIZE = 20

const PUBLICATION_PATHS = [
  '/ChatSocket',
  '/agent/chat',
  '/campaign/chat',
  '/NewPublicationPageIA',
  '/NewPublicationPageMyPublication',
  '/NewPublicationPage',
  '/NewPublicationPageCampaign',
]

const CollapsedNavIcon = ({
  label,
  icon: Icon,
  isActive,
  onClick,
  disabled,
  to,
  state,
  iconSize = NAV_ICON_SIZE,
}) => {
  const iconColor = isActive ? NAV_ICON_ACTIVE : NAV_ICON_COLOR
  const className = `sidebar-collapsed-nav-btn${isActive ? ' sidebar-collapsed-nav-btn-active' : ''}`

  if (to) {
    return (
      <Link
        to={to}
        state={state}
        className={className}
        aria-label={label}
        title={label}
      >
        <Icon color={iconColor} width={iconSize} height={iconSize} size={iconSize} />
      </Link>
    )
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      <Icon color={iconColor} width={iconSize} height={iconSize} size={iconSize} />
    </button>
  )
}

const SidebarNavigation = ({ variant = 'expanded' }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { userData } = useAuth()

  const {
    handleNavigate,
    loading,
    blockDowngrade,
    handleCloseDowngradeModal,
  } = useNavigateWithValidation()

  const [hasScheduledPublications, setHasScheduledPublications] = useState(false)
  const [isPublicationOpen, setIsPublicationOpen] = useState(false)

  const isSuperAdmin = Boolean(
    userData?.userAccount?.[0]?.superAdmin ||
      userData?.userAccount?.some((account) => account?.superAdmin),
  )

  const isProductionStage =
    String(import.meta.env.VITE_STAGE || import.meta.env.STAGE || '').toLowerCase() ===
    'production'

  const isPublicationActive = PUBLICATION_PATHS.some(
    (path) => location.pathname.toLowerCase() === path.toLowerCase(),
  )

  useEffect(() => {
    if (!userData) {
      return
    }

    checkScheduledPublications()
  }, [userData])

  const checkScheduledPublications = async () => {
    try {
      const response = await PublicationService.getAllPublicationByLogin()
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        setHasScheduledPublications(true)
      } else {
        setHasScheduledPublications(false)
      }
    } catch (error) {
      console.error('Erro ao verificar publicações agendadas:', error)
      setHasScheduledPublications(false)
    }
  }

  const handlePublicationOptionClick = async (route) => {
    await handleNavigate(route)
  }

  const publicationItems = [
    {
      label: t('campaign_header.publication_dropdown.generate_campaign'),
      route: '/ChatSocket',
      icon: Magic,
    },
    {
      label: t('campaign_header.publication_dropdown.generate_art'),
      route: '/NewPublicationPageIA',
      icon: Picture,
    },
    {
      label: t('campaign_header.publication_dropdown.schedule_image'),
      route: '/NewPublicationPageMyPublication',
      icon: Datepiker,
    },
  ]

  const navItems = [
    {
      label: t('side_menu.visual_identity'),
      path: '/FastPublishing1',
      state: { parametersComplete: true },
      icon: VisualIdentity,
    },
    {
      label: t('side_menu.my_account'),
      path: '/MyAccountPage',
      icon: UserCircle,
    },
    {
      label: t('side_menu.plans'),
      path: '/PlanCheckoutUser',
      icon: CurrencyDollar,
    },
    ...(hasScheduledPublications
      ? [{
        label: t('side_menu.calendar'),
        path: '/SchedulePublicationPage',
        icon: Calendar,
        activePaths: ['/SchedulePublicationPage', '/SchedulingPage'],
      }]
      : []),
    ...(isSuperAdmin && !isProductionStage
      ? [{
        label: t('side_menu.ai_manager'),
        path: '/AIManager',
        icon: Target,
        activePaths: ['/AIManager', '/PromptManagerList', '/PromptManagerCreate'],
        activePathPrefix: '/PromptManagerEdit/',
      }]
      : []),
  ]

  const isPathActive = (path, activePaths, activePathPrefix) => {
    const currentPath = location.pathname

    if (activePaths?.some((activePath) => currentPath.toLowerCase() === activePath.toLowerCase())) {
      return true
    }

    if (activePathPrefix && currentPath.toLowerCase().startsWith(activePathPrefix.toLowerCase())) {
      return true
    }

    return currentPath.toLowerCase() === String(path || '').toLowerCase()
  }

  const renderNavRow = ({
    label,
    icon: Icon,
    isActive,
    onClick,
    disabled,
    to,
    state,
    key,
    className = '',
    trailing,
    iconSize = NAV_ICON_SIZE,
  }) => {
    const iconColor = isActive ? NAV_ICON_ACTIVE : NAV_ICON_COLOR
    const rowClassName = `sidebar-nav-row${isActive ? ' sidebar-nav-row-active' : ''}${className ? ` ${className}` : ''}`

    if (to) {
      return (
        <li key={key}>
          <Link to={to} state={state} className={rowClassName}>
            <span className="sidebar-nav-row-icon">
              <Icon color={iconColor} width={iconSize} height={iconSize} size={iconSize} />
            </span>
            <span className="sidebar-nav-row-label">{label}</span>
            {trailing}
          </Link>
        </li>
      )
    }

    return (
      <li key={key}>
        <button
          type="button"
          className={rowClassName}
          onClick={onClick}
          disabled={disabled}
        >
          <span className="sidebar-nav-row-icon">
            <Icon color={iconColor} width={iconSize} height={iconSize} size={iconSize} />
          </span>
          <span className="sidebar-nav-row-label">{label}</span>
          {trailing}
        </button>
      </li>
    )
  }

  if (variant === 'collapsed') {
    return (
      <>
        <ModalValidDowngrade
          isOpen={blockDowngrade}
          onClose={handleCloseDowngradeModal}
          handleCloseModal={handleCloseDowngradeModal}
        />

        <nav className="sidebar-collapsed-nav-menu" aria-label={t('side_menu.publication')}>
          <CollapsedNavIcon
            label={t('side_menu.publication')}
            icon={Picture}
            isActive={isPublicationActive}
            onClick={() => handlePublicationOptionClick('/ChatSocket')}
            disabled={loading}
          />

          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = isPathActive(item.path, item.activePaths, item.activePathPrefix)

            return (
              <CollapsedNavIcon
                key={`${item.path}-${item.label}`}
                label={item.label}
                icon={Icon}
                isActive={isActive}
                to={item.path}
                state={item.state}
                iconSize={item.iconSize}
              />
            )
          })}
        </nav>
      </>
    )
  }

  return (
    <>
      <ModalValidDowngrade
        isOpen={blockDowngrade}
        onClose={handleCloseDowngradeModal}
        handleCloseModal={handleCloseDowngradeModal}
      />

      <nav className="sidebar-nav-menu" aria-label={t('side_menu.publication')}>
        <ul className="sidebar-nav-menu-list">
          <li className="sidebar-nav-dropdown">
            <button
              type="button"
              className={`sidebar-nav-row sidebar-nav-dropdown-trigger${isPublicationActive ? ' sidebar-nav-row-active' : ''}${isPublicationOpen ? ' sidebar-nav-dropdown-open' : ''}`}
              onClick={() => setIsPublicationOpen((prev) => !prev)}
              aria-expanded={isPublicationOpen}
            >
              <span className="sidebar-nav-row-icon">
                <Picture
                  color={isPublicationActive ? NAV_ICON_ACTIVE : NAV_ICON_COLOR}
                  width={NAV_ICON_SIZE}
                  height={NAV_ICON_SIZE}
                  size={NAV_ICON_SIZE}
                />
              </span>
              <span className="sidebar-nav-row-label">{t('side_menu.publication')}</span>
              <span className={`sidebar-nav-chevron${isPublicationOpen ? ' sidebar-nav-chevron-open' : ''}`}>
                <ChevronDown
                  size={14}
                  color={isPublicationActive || isPublicationOpen ? NAV_ICON_ACTIVE : NAV_ICON_COLOR}
                />
              </span>
            </button>

            <div
              className={`sidebar-nav-collapse${isPublicationOpen ? ' sidebar-nav-collapse-open' : ''}`}
              aria-hidden={!isPublicationOpen}
            >
              <ul className="sidebar-nav-submenu">
                {publicationItems.map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname.toLowerCase() === item.route.toLowerCase()

                  return renderNavRow({
                    key: item.route,
                    label: item.label,
                    icon: Icon,
                    isActive,
                    onClick: () => handlePublicationOptionClick(item.route),
                    disabled: loading,
                    className: 'sidebar-nav-submenu-item',
                  })
                })}
              </ul>
            </div>
          </li>

          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = isPathActive(item.path, item.activePaths, item.activePathPrefix)

            return renderNavRow({
              key: `${item.path}-${item.label}`,
              label: item.label,
              icon: Icon,
              isActive,
              to: item.path,
              state: item.state,
              iconSize: item.iconSize,
            })
          })}
        </ul>
      </nav>
    </>
  )
}

export default SidebarNavigation
